import {
  type ElementType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate } from "react-router-dom";

import PageLayout from "@/components/layouts/page/PageLayout";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { subscriptionService } from "@/features/subscriptions/services/subscriptionService";
import SubscriptionOverview from "../components/SubscriptionOverview";
import UserSubscriptions from "../components/UserSubscriptions";
import OptedOutSubscriptions from "../components/OptedOutSubscriptions";
import PageHeader from "@/components/layouts/pageHeaders/PageHeader";
import { Button } from "@mui/material";
import useFetcher from "@/helpers/hooks/useFetcher";
import { USER_ROLE } from "@/config/constants";
import { getDashboardPath } from "@/helpers/utils";

type SubscriptionRecord = Record<string, any>;
type GlobalContextValue = {
  loggedInUser: SubscriptionRecord;
  loggedInUserRole?: string;
  selectedBrand?: SubscriptionRecord;
};
type Loader = (_show: boolean) => void;
type SubscriptionFilters = {
  brandId?: string | number;
  download: number;
  groupKey: string;
  offset: number;
  page: number;
  searchKey: string;
  sortBy: string;
  sortByDir: string;
  statusKey: string;
  subsLenKey: string;
  userId?: string | number;
};
type FetchDataParams = {
  filters?: SubscriptionRecord;
  pageIndex: number;
  pageSize: number;
  searchKey?: string;
  sortBy?: string;
  sortByDir?: string;
};

const PageLayoutComponent = PageLayout as ElementType;
const PageHeaderComponent = PageHeader as ElementType;
const SubscriptionOverviewComponent = SubscriptionOverview as ElementType;
const UserSubscriptionsComponent = UserSubscriptions as ElementType;
const OptedOutSubscriptionsComponent = OptedOutSubscriptions as ElementType;

export default function UserLocationsSubscriptions() {
  const { loggedInUser, loggedInUserRole, selectedBrand } = useContext(
    GlobalContext
  ) as GlobalContextValue;

  const loader = useContext(LoaderContext) as Loader;

  // const [locationStats, setLocationStats] = useState(null);
  // const [groupList, setGroupList] = useState([]);
  // const [isBrandUser, setIsBrandUser] = useState(false);
  const [showOptOut, setShowOptOut] = useState(false);

  const is_brand_auto_renew_on = selectedBrand?.is_auto_renewal;

  // filters common state
  const [filters, setFilters] = useState<SubscriptionFilters>({
    userId: loggedInUser.id,
    brandId: selectedBrand?.id,
    download: 0,
    page: 1,
    offset: 10,
    searchKey: "",
    groupKey: "",
    statusKey: "",
    subsLenKey: "",
    sortBy: "",
    sortByDir: "",
  });

  const reqData = useMemo(() => {
    return {
      ...filters,
      userId: loggedInUser.id,
      brandId: selectedBrand?.id,
      download: 0,
    };
  }, [filters, loggedInUser.id, selectedBrand?.id]);

  const [prevData, setPrevData] = useState<SubscriptionRecord | null>(null);

  const { data, isLoading: loading } = useFetcher(
    reqData,
    subscriptionService.getUserLocationsSubscriptionDetail,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (data) {
      setPrevData(data);
    }
  }, [data]);

  const { locationData, topRowCountData, isBrandUser } = data || prevData || {};

  const locationStats = {
    subscribedLocationCount: topRowCountData?.subscribedLocationCount,
    unSubscribedLocationCount: topRowCountData?.unSubscribedLocationCount,
    upcomingRenewalCount: topRowCountData?.upcomingRenewalCount,
  };

  const groupList = topRowCountData?.groupList || [];

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  useEffect(() => {
    if (selectedBrand?.id) {
      setShowOptOut(false);
    }
  }, [selectedBrand?.id]);

  // Reset filters
  useEffect(() => {
    if (!showOptOut) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        groupKey: "",
        searchKey: "",
        statusKey: "",
        subsLenKey: "",
        brandId: selectedBrand?.id,
        userId: loggedInUser.id,
      }));
    }
  }, [showOptOut, selectedBrand?.id, loggedInUser.id]);

  const handleFetchData = useCallback(
    (data: FetchDataParams) => {
      const {
        pageIndex,
        pageSize,
        searchKey,
        filters: fetchFilters,
        sortBy,
        sortByDir,
      } = data;

      const reqData = {
        ...fetchFilters,
        download: 0,
        page: pageIndex + 1,
        offset: pageSize,
        searchKey: searchKey || "",
        sortBy,
        sortByDir,
        brandId: selectedBrand?.id,
        userId: loggedInUser.id,
      };

      setFilters((f) => ({ ...f, ...reqData }));
    },
    [selectedBrand?.id, loggedInUser.id]
  );

  if (loggedInUserRole === USER_ROLE.PARTICIPANT.NAME) {
    return <Navigate to={`${getDashboardPath()}`} replace />;
  }

  return (
    <PageLayoutComponent className="user-locations-subscriptions-container mx-0 mx-sm-2">
      <PageHeaderComponent title="Media Plan Overview" showHr={false} />
      <div className="user-locations-subscriptions mt-2">
        <SubscriptionOverviewComponent
          locationStats={locationStats}
          loggedInUserRole={loggedInUserRole}
          selectedBrand={selectedBrand}
          isBrandUser={isBrandUser}
        />

        <div className="user-subscription-list my-5">
          <div className="user-subscriptions">
            <div className="text-right float-right mt-4">
              {showOptOut ? (
                <Button
                  variant="contained"
                  className="table-btn-link"
                  disableElevation
                  onClick={() => {
                    setShowOptOut(false);
                  }}
                >
                  View Plan Table
                </Button>
              ) : (
                <>
                  {is_brand_auto_renew_on ? (
                    <Button
                      variant="contained"
                      className="table-btn-link"
                      disableElevation
                      onClick={() => {
                        setShowOptOut(true);
                      }}
                    >
                      View Opt-Out Table
                    </Button>
                  ) : null}
                </>
              )}
            </div>
            {showOptOut ? (
              <OptedOutSubscriptionsComponent />
            ) : (
              <UserSubscriptionsComponent
                isLoading={loading}
                locationData={locationData}
                fetchData={handleFetchData}
                groupList={groupList}
                filtersData={filters}
                is_brand_auto_renew_on={is_brand_auto_renew_on}
              />
            )}
          </div>
        </div>
      </div>
    </PageLayoutComponent>
  );
}
